import _ from 'lodash'
import React, {Component} from 'react'
import {string, number, shape, oneOf} from 'prop-types'
import glamorous, {withTheme} from 'glamorous'

import GeocodingClient from '@mapbox/mapbox-sdk/services/geocoding'
import mapboxgl from 'mapbox-gl'
import * as turf from '@turf/turf'

import {colors, getThemeStyles} from '../css/themes'

const mapboxAccessToken =
  'pk.eyJ1IjoianJ5aW8iLCJhIjoiY2pmY3B4bjhkMW42MjMzb2Zkc3JoNWNrNyJ9.zIp9h3epjar5s5HbHJFEeQ'
const mapboxStyleLight = 'mapbox://styles/mapbox/light-v9'
const mapboxStyleDark = 'mapbox://styles/mapbox/dark-v9'

const MapContainer = glamorous.div(
  {
    position: 'relative',
    width: '100%',
    minHeight: '20rem',
    marginTop: '2rem',
    marginBottom: '2rem',
  },
  getThemeStyles('map')
)

class MapComponent extends Component {
  static propTypes = {
    // Theme
    theme: shape({type: string.isRequired}),
    // Map Types
    type: oneOf(['flight', 'location']).isRequired,
    // Flight Props
    start: string,
    end: string,
    // Location Props
    location: string,
    zoom: number,
  }

  mapRef = null
  map = null
  client = null

  /*
   * Helpers
   */
  _forwardGeocode = (locations, limit = 1) => {
    return Promise.all(
      _.map(locations, location =>
        this.client
          .forwardGeocode({query: location, mode: 'mapbox.places'}, {limit, autocomplete: false})
          .send()
      )
    )
  }

  _computeBoundingBox = features => {
    const points = _.map(features, 'center')
    const lineString = turf.lineString(points)
    const boundingBox = turf.bbox(lineString)
    return boundingBox
  }

  _fitMapToBounds = boundingBox => {
    this.map.fitBounds(boundingBox, {linear: true, padding: {top: 0, bottom: 0, left: 20, right: 20}})
  }

  /*
   * Map Initialization
   */
  _initMapbox = theme => {
    // Mapbox API client for geocode searching
    this.client = new GeocodingClient({accessToken: mapboxAccessToken})

    // MapboxGL instance
    mapboxgl.accessToken = mapboxAccessToken
    this.map = new mapboxgl.Map({
      container: this.mapRef,
      style: theme,
      scrollZoom: false,
    })
  }

  _initFlight = () => {
    this._forwardGeocode([this.props.start, this.props.end]).then(geodata => {
      const locationsFeatures = _.map(geodata, data => _.get(data, 'body.features[0]'))
      this._renderMapFlight(locationsFeatures)
    })
  }

  _initLocation = () => {
    this._forwardGeocode([this.props.location]).then(geodata => {
      const feature = _.get(_.first(geodata), 'body.features[0]')
      this._renderMapLocation(feature)
    })
  }

  /*
   * Map Render
   */
  _renderMapLocation = feature => {
    const origin = feature.center

    this.map.on('load', () => {
      this.map.addSource('single-location', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: [feature],
        },
      })
      this.map.addLayer({
        id: 'point',
        source: 'single-location',
        type: 'circle',
        paint: {
          'circle-radius': 5,
          'circle-color': colors.orange,
        },
      })
      this._fitMapToBounds(feature.bbox)
      // const marker = new mapboxgl.Marker({anchor: 'center'})
      // marker.setLngLat(origin).addTo(this.map)
    })
  }

  _renderMapFlight = features => {
    const origin = features[0].center
    const destination = _.last(features).center
    const route = {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: [origin, destination],
          },
        },
      ],
    }

    const point = {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: destination,
          },
        },
      ],
    }

    // Draw an arc between the `origin` & `destination` of the two points
    const lineDistance = turf.distance(origin, destination, {units: 'kilometers'})
    const arc = _.reduce(
      _.range(lineDistance),
      (array, i) => [
        ...array,
        turf.along(route.features[0], (i / 1000) * lineDistance, {units: 'kilometers'}).geometry.coordinates,
      ],
      []
    )

    // Update the route with calculated arc coordinates
    route.features[0].geometry.coordinates = arc

    const boundingBox = this._computeBoundingBox(features)
    this._fitMapToBounds(boundingBox)

    this.map.on('resize', () => {
      this._fitMapToBounds(boundingBox)
    })
    this.map.on('load', () => {
      this.map.addSource('route', {
        type: 'geojson',
        data: route,
      })

      this.map.addSource('point', {
        type: 'geojson',
        data: point,
      })

      this.map.addLayer({
        id: 'route',
        source: 'route',
        type: 'line',
        paint: {
          'line-width': 2,
          'line-color': '#007cbf',
        },
      })

      // Plane
      this.map.addLayer({
        id: 'point',
        source: 'point',
        type: 'symbol',
        layout: {
          'icon-image': 'airport-15',
          'icon-rotate': 90,
          'icon-allow-overlap': true,
        },
      })
    })
  }

  /*
   * Lifecycle Methods
   */
  componentDidMount() {
    if (!this.mapRef) return

    // Attempt to get local theme
    const theme = this.props.theme.type === 'dark' ? mapboxStyleDark : mapboxStyleLight

    // Initialize Map instance
    this._initMapbox(theme)

    if (this.props.type === 'flight' && this.props.start && this.props.end) {
      return this._initFlight()
    }

    if (this.props.type === 'location' && this.props.location) {
      return this._initLocation()
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.theme && prevProps.theme && this.props.theme.type !== prevProps.theme.type) {
      if (this.map) {
        const theme = this.props.theme.type === 'dark' ? mapboxStyleDark : mapboxStyleLight
        this.map.setStyle(theme, {diff: false})
      }
    }
  }

  componentWillUnmount() {
    this.mapRef = null
  }

  /*
   * Render Methods
   */
  render() {
    return (
      <MapContainer>
        <div id="mapContainer" style={styleMap} ref={el => (this.mapRef = el)} />
      </MapContainer>
    )
  }
}

const styleMap = {
  position: 'absolute',
  top: 0,
  bottom: 0,
  right: 0,
  left: 0,
}

export default withTheme(MapComponent)
